<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useField } from 'vee-validate'
import type { FormField } from '../types/formTypeStructure'
import {
  useResponsiveSize,
  getLocalizedText
} from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import { AlertCircle } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'textarea' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: string];
  'change': [value: string];
  'input': [value: string];
}>();

const fieldName = props.name ?? props.field.fieldName.toString()
const size = computed(() => useResponsiveSize().calculateSize(window.innerWidth))
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage,
  handleChange,
  setTouched
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

// Émettre les événements quand la valeur change
watch(value, (newValue) => {
  emit('update:modelValue', newValue);
  emit('change', newValue);
  emit('input', newValue);
});

const placeholder = getLocalizedText(props.field.placeholder, props.currentLang ?? 'fr')

const inputStyle = computed(() => ({
  width: `${size.value.width}px`,
  height: `${size.value.height * 2}px`,
  borderRadius: `${size.value.borderRadius}px`,
  resize: 'vertical' as const,
  border: '2px solid #04406E',
  outlineColor: '#04406E',
  color: '#04406E',
  padding: '10px',
  fontSize: '1rem',
  backgroundColor: '#f8ffff',
  transition: 'border-color 0.2s, box-shadow 0.2s'
}))

const baseClasses = 'transition-all focus:ring-2 focus:ring-[#04406E]'
</script>

<template>
  <div class="textarea-field-container">
    <!-- Label dupliqué au-dessus du textarea -->
    <div class="textarea-field-label-container">
      <span class="textarea-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <textarea
        :value="value"
        :placeholder="placeholder"
        @input="(e) => { handleChange((e.target as HTMLTextAreaElement).value); setTouched(true); }"
        @blur="() => setTouched(true)"
        :class="[baseClasses, props.className]"
        :style="inputStyle"
      />
      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.textarea-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.textarea-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.textarea-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>
